<template>
  <div class="emh-ui-accordion-item">
    <button class="emh-ui-accordion-item__head" data-test-id="emh-ui-accordion-item__head" @click="onToggle">
      <div class="emh-ui-accordion-item__head-content">
        <slot name="head"></slot>
      </div>
      <div class="emh-ui-accordion-item__head-icon">
        <wb-icon :name.attr="isOpen ? 'bds/chevron-up/16' : 'bds/chevron-down/16'" />
      </div>
    </button>
    <Transition name="expand" @after-enter="onExpandingFinished" @after-leave="onExpandingFinished">
      <div
        v-if="isOpen"
        ref="contentEl"
        class="emh-ui-accordion-item__content"
        data-test-id="emh-ui-accordion-item__content"
      >
        <slot name="content"></slot>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
export default { name: 'UiAccordionItem' };
</script>

<script lang="ts" setup>
import { nextTick, ref, toRefs, watch } from 'vue';

const props = defineProps({
  open: { type: Boolean, required: false, default: false },
});

const contentEl = ref<HTMLDivElement | null>(null);

const emit = defineEmits(['toggle']);

const { open: isOpenProp } = toRefs(props);
const isOpen = ref<boolean>(isOpenProp.value);
const height = ref<number | undefined>(undefined);

watch(isOpenProp, () => {
  if (isOpen.value !== isOpenProp.value) {
    isOpen.value = isOpenProp.value;
    runExpandAnimation();
  }
});

const onToggle = async () => {
  isOpen.value = !isOpen.value;
  await runExpandAnimation();
  emit('toggle', isOpen.value);
};

const runExpandAnimation = async () => {
  if (isOpen.value) await nextTick();

  height.value = contentEl.value?.scrollHeight;

  if (height.value && contentEl.value) contentEl.value.style.maxHeight = height.value + 'px';
};

const onExpandingFinished = () => {
  if (contentEl.value) contentEl.value.style.maxHeight = 'inherit';
};
</script>
<style lang="scss" scoped>
.emh-ui-accordion-item {
  border-bottom: 1px solid var(--wb-grey-85);

  &__head {
    width: 100%;
    display: flex;
    justify-content: space-between;
    background: var(--wb-white);
    cursor: pointer;
    border: none;
    padding: 0;

    &:hover {
      .emh-ui-accordion-item__head-icon {
        background-color: var(--wb-grey-90);
      }
    }
  }

  &__head-content {
    padding: var(--wb-spacing-xs) 0;

    @include wb-type-copy-strong;
  }

  &__head-icon {
    align-self: center;
    display: flex;
    justify-content: center;
    border-radius: 100%;
    transition: background-color 0.3s var(--wb-fade);
    width: 24px;
    height: 24px;

    wb-icon {
      align-self: center;
      width: 16px;
      height: 16px;
    }
  }

  &__content {
    padding: 0 var(--wb-spacing-xxs);
    overflow: hidden;
  }

  .expand {
    &-enter-active,
    &-leave-active {
      transition: max-height 0.3s var(--wb-fade);
    }

    &-enter-from,
    &-leave-to {
      max-height: 0 !important;
    }
  }
}
</style>
